import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Users, Plus, Trash2, Edit, HardHat, User, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { isAffectedForTeamsPage } from "@/lib/disponibilite-utils";
import { usePeriodSync } from "@/hooks/usePeriodSync";
import type { JourDetail } from "@/types/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { PictogrammeEquipeSelector } from "@/components/equipe/PictogrammeEquipeSelector";
import { CouleurSelector } from "@/components/chantiers/CouleurSelector";
import { EquipeIcon } from "@/components/equipe/EquipeIcon";
import { getPictogrammeEquipeById } from "@/constants/pictogrammesEquipe";
import { cn } from "@/lib/utils";
import { BulkEquipeModal } from "./BulkEquipeModal";
import { z } from "zod";

// Schema de validation pour la creation/edition d'equipe
const equipeFormSchema = z.object({
  nom: z.string().min(1, "Le nom de l'équipe est requis").max(100, "100 caractères max"),
  description: z.string().max(500, "500 caractères max"),
  specialite: z.string().max(100, "100 caractères max"),
  pictogramme: z.string().min(1, "Le pictogramme est requis"),
  couleur: z.string().min(1, "La couleur est requise"),
});

interface Equipe {
  id: string;
  nom: string;
  description: string;
  specialite: string;
  created_at: string;
  membres_count?: number;
  pictogramme?: string;
  couleur?: string;
}

interface ChantierAffectation {
  chantierRef: string;
  count: number;
  dateFin: string;
  isRetard: boolean;
}

interface AffectationEquipe {
  chantiers: string[];
  dateFin: string;
  chantierMajoritaire?: string;
  dateFinMajoritaire?: string;
  isRetard?: boolean; // true si l'affectation majoritaire est un retard
  // Détails par chantier pour affichage ventilé
  chantiersDetails: ChantierAffectation[];
}

interface DisponibiliteEquipe {
  disponibles: number;
  total: number;
}

interface TeamManagerProps {
  entrepriseId: string;
  addMemberButton?: React.ReactNode;
  onEquipeChange?: (newEquipe?: { id: string; nom: string; specialite: string }) => void;
}

const TeamManager = ({ entrepriseId, addMemberButton, onEquipeChange }: TeamManagerProps) => {
  const { toast } = useToast();
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [affectationsEquipes, setAffectationsEquipes] = useState<Record<string, AffectationEquipe>>({});
  const [disponibiliteEquipes, setDisponibiliteEquipes] = useState<Record<string, DisponibiliteEquipe>>({});
  const [dialogOpen, setDialogOpen] = useState(false); // Pour l'édition uniquement
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false); // Pour la création bulk
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeletingEquipe, setIsDeletingEquipe] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingEquipe, setEditingEquipe] = useState<Equipe | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nom: "",
    description: "",
    specialite: "",
    pictogramme: "Wrench",
    couleur: "#b45309",
  });

  // Hook de synchronisation de période (13h, 17h, minuit)
  const { refreshKey } = usePeriodSync();

  // Rechargement des équipes quand entrepriseId change OU quand la période change (13h, 17h, minuit)
  useEffect(() => {
    loadEquipes();
  }, [entrepriseId, refreshKey]);

  // Écouter les événements de mise à jour des chantiers pour synchroniser les affectations
  useEffect(() => {
    const handleMetricsUpdate = () => {
      if (import.meta.env.DEV) console.log('[TeamManager] Événement chantier-metrics-updated reçu, rechargement...');
      loadEquipes();
    };

    window.addEventListener('chantier-metrics-updated', handleMetricsUpdate);
    return () => window.removeEventListener('chantier-metrics-updated', handleMetricsUpdate);
  }, []);

  // Abonnement Realtime pour équipes et membres
  useEffect(() => {
    if (!entrepriseId) return;

    if (import.meta.env.DEV) console.log('[TeamManager] Création abonnement Realtime pour entreprise:', entrepriseId);

    // Canal pour les équipes (avec filtre - fonctionne pour INSERT/UPDATE/DELETE)
    const equipesChannel = supabase
      .channel(`equipes_realtime:${entrepriseId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'equipes',
          filter: `entreprise_id=eq.${entrepriseId}`,
        },
        (payload) => {
          if (import.meta.env.DEV) console.log('[TeamManager] Changement détecté sur equipes:', payload);
          loadEquipes();
        }
      )
      .subscribe((status) => {
        if (import.meta.env.DEV) console.log('[TeamManager] Statut abonnement equipes:', status);
      });

    // Canal séparé pour les membres (filtré par entreprise_id)
    const membresChannel = supabase
      .channel(`membres_realtime:${entrepriseId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'membres_equipe',
          filter: `entreprise_id=eq.${entrepriseId}`,
        },
        (payload) => {
          if (import.meta.env.DEV) console.log('[TeamManager] Changement détecté sur membres_equipe:', payload);
          loadEquipes();
        }
      )
      .subscribe((status) => {
        if (import.meta.env.DEV) console.log('[TeamManager] Statut abonnement membres:', status);
      });

    return () => {
      if (import.meta.env.DEV) console.log('[TeamManager] Suppression abonnements Realtime');
      supabase.removeChannel(equipesChannel);
      supabase.removeChannel(membresChannel);
    };
  }, [entrepriseId]);

  const loadEquipes = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      const [equipesRes, affectationsRes, membresRes] = await Promise.all([
        supabase
          .from("equipes")
          .select("*")
          .eq("entreprise_id", entrepriseId)
          .order("created_at", { ascending: false }),
        // Charger les affectations actives avec les infos du chantier ET du membre (+ notes pour détecter retard + jours_details pour AM/PM)
        supabase
          .from("affectations_chantiers")
          .select("equipe_id, membre_equipe_id, date_debut, date_fin, chantier_id, notes, jours_details, chantiers(reference_chantier, nom_chantier)")
          .gte("date_fin", today),
        // Charger tous les membres avec leur statut pour calculer la disponibilité
        supabase
          .from("membres_equipe")
          .select("id, equipe_id, statut")
          .eq("entreprise_id", entrepriseId),
      ]);

      if (equipesRes.error) throw equipesRes.error;

      // Construire un map equipe_id -> { chantiers[], dateFin, chantierMajoritaire, dateFinMajoritaire }
      const affectationsMap: Record<string, AffectationEquipe> = {};

      // D'abord, regrouper les membres de chaque équipe
      const membresParEquipe: Record<string, string[]> = {};
      if (membresRes.data) {
        for (const membre of membresRes.data) {
          if (membre.equipe_id) {
            if (!membresParEquipe[membre.equipe_id]) {
              membresParEquipe[membre.equipe_id] = [];
            }
            membresParEquipe[membre.equipe_id].push(membre.id);
          }
        }
      }

      if (affectationsRes.data) {
        // Regrouper les affectations par équipe puis par chantier pour compter les membres
        const affectationsParEquipeChantier: Record<string, Record<string, { count: number; dateFin: string; chantierRef: string; isRetard: boolean }>> = {};

        for (const aff of affectationsRes.data) {
          // Vérifier que l'affectation couvre aujourd'hui ET la période actuelle (AM/PM)
          const dateDebutAff = aff.date_debut || today;
          if (dateDebutAff > today || aff.date_fin < today) continue; // Pas aujourd'hui

          const joursDetails = (aff as any).jours_details as JourDetail[] | null;
          if (!isAffectedForTeamsPage(joursDetails, today)) continue; // Pas cette période

          // Récupérer l'équipe soit directement, soit via le membre
          let equipeId = aff.equipe_id;
          if (!equipeId && aff.membre_equipe_id) {
            // Trouver l'équipe du membre
            const membre = membresRes.data?.find(m => m.id === aff.membre_equipe_id);
            equipeId = membre?.equipe_id;
          }

          if (equipeId && aff.chantiers && aff.chantier_id) {
            const chantierInfo = aff.chantiers as any;
            const chantierRef = chantierInfo.reference_chantier || chantierInfo.nom_chantier;
            // Détecter si c'est une affectation de retard via le champ notes
            const isRetard = (aff as any).notes?.startsWith('Retard chantier') || false;

            if (!affectationsParEquipeChantier[equipeId]) {
              affectationsParEquipeChantier[equipeId] = {};
            }

            if (!affectationsParEquipeChantier[equipeId][aff.chantier_id]) {
              affectationsParEquipeChantier[equipeId][aff.chantier_id] = {
                count: 0,
                dateFin: aff.date_fin,
                chantierRef,
                isRetard,
              };
            }

            affectationsParEquipeChantier[equipeId][aff.chantier_id].count++;
            // Garder la date de fin la plus tardive pour ce chantier
            if (aff.date_fin > affectationsParEquipeChantier[equipeId][aff.chantier_id].dateFin) {
              affectationsParEquipeChantier[equipeId][aff.chantier_id].dateFin = aff.date_fin;
            }
            // Si une affectation est un retard, marquer le chantier comme retard
            if (isRetard) {
              affectationsParEquipeChantier[equipeId][aff.chantier_id].isRetard = true;
            }
          }
        }

        // Maintenant, pour chaque équipe, trouver le chantier majoritaire + construire les détails
        for (const equipeId of Object.keys(affectationsParEquipeChantier)) {
          const chantiersData = affectationsParEquipeChantier[equipeId];
          const chantiersList: string[] = [];
          const chantiersDetails: ChantierAffectation[] = [];
          let dateFinMax = "";
          let chantierMajoritaire = "";
          let dateFinMajoritaire = "";
          let maxCount = 0;
          let isRetardMajoritaire = false;

          for (const chantierId of Object.keys(chantiersData)) {
            const data = chantiersData[chantierId];
            chantiersList.push(data.chantierRef);

            // Ajouter aux détails pour affichage ventilé
            chantiersDetails.push({
              chantierRef: data.chantierRef,
              count: data.count,
              dateFin: data.dateFin,
              isRetard: data.isRetard,
            });

            // Date de fin la plus tardive globale
            if (data.dateFin > dateFinMax) {
              dateFinMax = data.dateFin;
            }

            // Chantier avec le plus de membres (majoritaire)
            if (data.count > maxCount) {
              maxCount = data.count;
              chantierMajoritaire = data.chantierRef;
              dateFinMajoritaire = data.dateFin;
              isRetardMajoritaire = data.isRetard;
            }
          }

          // Trier les détails par nombre de membres décroissant
          chantiersDetails.sort((a, b) => b.count - a.count);

          affectationsMap[equipeId] = {
            chantiers: [...new Set(chantiersList)], // Dédupliquer
            dateFin: dateFinMax,
            chantierMajoritaire,
            dateFinMajoritaire,
            isRetard: isRetardMajoritaire,
            chantiersDetails,
          };
        }
      }
      setAffectationsEquipes(affectationsMap);

      // Calculer la disponibilité par équipe basée sur les affectations actives
      const disponibiliteMap: Record<string, DisponibiliteEquipe> = {};

      // Construire un set des membres qui ont une affectation active POUR LA PÉRIODE ACTUELLE (matin ou après-midi)
      const membresOccupes = new Set<string>();
      if (affectationsRes.data) {
        for (const aff of affectationsRes.data) {
          if (aff.membre_equipe_id) {
            // L'affectation est déjà filtrée pour être >= aujourd'hui (date_fin >= today)
            // Mais on doit aussi vérifier que date_debut <= today pour être "actuellement occupé"
            const dateDebutAff = aff.date_debut || today;
            if (dateDebutAff <= today && aff.date_fin >= today) {
              // Vérifier si l'affectation couvre la période actuelle (AM/PM) via jours_details
              const joursDetails = (aff as any).jours_details as JourDetail[] | null;
              if (isAffectedForTeamsPage(joursDetails, today)) {
                membresOccupes.add(aff.membre_equipe_id);
              }
            }
          }
        }
      }

      if (membresRes.data) {
        for (const membre of membresRes.data) {
          if (membre.equipe_id) {
            if (!disponibiliteMap[membre.equipe_id]) {
              disponibiliteMap[membre.equipe_id] = { disponibles: 0, total: 0 };
            }
            disponibiliteMap[membre.equipe_id].total++;
            // Un membre est disponible s'il:
            // 1. N'a pas d'affectation active aujourd'hui ET
            // 2. Son statut n'est pas repos, en_arret ou en_rotation
            const isOccupe = membresOccupes.has(membre.id);
            const isIndisponible = membre.statut === 'repos' || membre.statut === 'en_arret' || membre.statut === 'en_rotation';
            if (!isOccupe && !isIndisponible) {
              disponibiliteMap[membre.equipe_id].disponibles++;
            }
          }
        }
      }
      setDisponibiliteEquipes(disponibiliteMap);

      // Charger le nombre de membres pour chaque équipe
      const equipesWithCount = await Promise.all(
        (equipesRes.data || []).map(async (equipe) => {
          const { count } = await supabase
            .from("membres_equipe")
            .select("*", { count: "exact", head: true })
            .eq("equipe_id", equipe.id);

          return {
            ...equipe,
            membres_count: count || 0,
          };
        })
      );

      setEquipes(equipesWithCount);
    } catch (error: any) {
      console.error("Erreur chargement équipes:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation Zod
    const validation = equipeFormSchema.safeParse(formData);
    if (!validation.success) {
      validation.error.issues.forEach(issue => {
        toast({ title: "Erreur", description: issue.message, variant: "destructive" });
      });
      return;
    }

    setLoading(true);

    try {
      if (editingEquipe) {
        const { error } = await supabase
          .from("equipes")
          .update(formData)
          .eq("id", editingEquipe.id);

        if (error) throw error;
        toast({ title: "Équipe modifiée", description: "Les modifications ont été enregistrées." });
      } else {
        const { data: newEquipe, error } = await supabase
          .from("equipes")
          .insert({
            ...formData,
            entreprise_id: entrepriseId,
          })
          .select('id, nom, specialite')
          .single();

        if (error) throw error;
        toast({ title: "Équipe créée", description: "La nouvelle équipe a été ajoutée." });

        // Notifier le parent avec la nouvelle équipe créée
        if (newEquipe) {
          onEquipeChange?.(newEquipe);
        }
      }

      resetForm();
      loadEquipes();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (equipe: Equipe) => {
    setEditingEquipe(equipe);
    setFormData({
      nom: equipe.nom,
      description: equipe.description || "",
      specialite: equipe.specialite || "",
      pictogramme: equipe.pictogramme || "Wrench",
      couleur: equipe.couleur || "#b45309",
    });
    setDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setIsDeletingEquipe(true);

    try {
      const { error } = await supabase
        .from("equipes")
        .delete()
        .eq("id", deleteId);

      if (error) throw error;

      toast({ title: "Équipe supprimée" });
      loadEquipes();
      // Notifier le parent que les équipes ont changé
      onEquipeChange?.();
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeletingEquipe(false);
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: "",
      description: "",
      specialite: "",
      pictogramme: "Wrench",
      couleur: "#b45309",
    });
    setEditingEquipe(null);
    setDialogOpen(false);
  };

  return (
    <>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={confirmDelete}
        variant="delete"
        isLoading={isDeletingEquipe}
      />

      <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-4 w-4" />
              Gestion des Équipes
            </CardTitle>
            <CardDescription className="text-xs">
              Créez et gérez vos équipes par spécialité
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {/* Bouton création bulk équipe */}
            <Button
              size="default"
              className={cn(
                "gap-1.5 font-semibold",
                equipes.length === 0 && "animate-highlight-pulse"
              )}
              onClick={() => setBulkCreateOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Créer équipe
            </Button>
            {addMemberButton}

            {/* Modal création bulk */}
            <BulkEquipeModal
              open={bulkCreateOpen}
              onOpenChange={setBulkCreateOpen}
              entrepriseId={entrepriseId}
              onSuccess={() => {
                // Recharger explicitement les équipes (Realtime peut être lent)
                loadEquipes();
                // Notifier le parent qu'une équipe a été créée
                onEquipeChange?.();
              }}
            />

            {/* Dialog d'édition uniquement */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogContent variant="industrial">
                <form onSubmit={handleSubmit}>
                  <DialogHeader>
                    <DialogTitle>Modifier l'équipe</DialogTitle>
                    <DialogDescription>
                      Modifiez le nom et la spécialité de l'équipe
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="nom">Nom de l'équipe *</Label>
                      <Input
                        id="nom"
                        placeholder="Ex: Équipe Maçonnerie"
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        required
                      />
                    </div>

                    {/* Pictogramme et Couleur */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Métier <span className="text-red-500">*</span></Label>
                        <PictogrammeEquipeSelector
                          value={formData.pictogramme}
                          onChange={(val) => setFormData({ ...formData, pictogramme: val })}
                          couleur={formData.couleur}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Couleur <span className="text-red-500">*</span></Label>
                        <CouleurSelector
                          value={formData.couleur}
                          onChange={(val) => setFormData({ ...formData, couleur: val })}
                        />
                      </div>
                    </div>

                    {/* Aperçu */}
                    <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                      <EquipeIcon
                        pictogramme={formData.pictogramme}
                        couleur={formData.couleur}
                        size="lg"
                      />
                      <div>
                        <p className="font-medium">{formData.nom || "Nom équipe"}</p>
                        <p className="text-sm text-muted-foreground">
                          {getPictogrammeEquipeById(formData.pictogramme)?.label || "Métier"}
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="specialite">Spécialité</Label>
                      <Input
                        id="specialite"
                        placeholder="Ex: Gros œuvre, Revêtement..."
                        value={formData.specialite}
                        onChange={(e) => setFormData({ ...formData, specialite: e.target.value })}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Description de l'équipe..."
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={resetForm} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">
                      Annuler
                    </Button>
                    <Button type="submit" disabled={loading} className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]">
                      {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {loading ? "Enregistrement..." : "Modifier"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {equipes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucune équipe créée</p>
            <p className="text-sm">Créez votre première équipe pour commencer</p>
          </div>
        ) : (
          <Table className="text-sm">
            <TableHeader>
              <TableRow className="h-9">
                <TableHead className="py-2">Nom</TableHead>
                <TableHead className="py-2">Spécialité</TableHead>
                <TableHead className="py-2">Affectation</TableHead>
                <TableHead className="py-2">Disponibilité</TableHead>
                <TableHead className="py-2">Nombre</TableHead>
                <TableHead className="text-right py-2">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {equipes.map((equipe) => (
                <TableRow key={equipe.id} className="h-10">
                  <TableCell className="font-medium py-2">
                    <div className="flex items-center gap-2">
                      <EquipeIcon
                        pictogramme={equipe.pictogramme || "Wrench"}
                        couleur={equipe.couleur || "#b45309"}
                        size="sm"
                      />
                      <span>{equipe.nom}</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    {equipe.specialite && (
                      <Badge variant="secondary" className="text-xs">{equipe.specialite}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="py-2">
                    {(() => {
                      const affectation = affectationsEquipes[equipe.id];

                      // Si affecté à un ou plusieurs chantiers
                      if (affectation && affectation.chantiersDetails?.length > 0) {
                        return (
                          <div className="flex flex-col gap-0.5">
                            {affectation.chantiersDetails.map((detail, idx) => {
                              // Orange pour retard, vert clair pour chantier normal
                              const colorClass = detail.isRetard ? 'text-amber-500' : 'text-white';
                              return (
                                <div key={idx} className="flex items-center gap-1.5">
                                  <span className={`${colorClass} font-medium text-xs`}>
                                    {detail.count}
                                  </span>
                                  <User className={`h-3 w-3 ${colorClass}`} />
                                  <span className={`${colorClass} font-medium text-xs`}>
                                    {detail.chantierRef}
                                  </span>
                                  <span className="text-zinc-400 text-xs font-medium">
                                    → {format(new Date(detail.dateFin), "dd/MM", { locale: fr })}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }

                      // Pas d'affectation
                      return (
                        <span className="text-green-500 text-xs font-medium">
                          Disponible
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="py-2">
                    {(() => {
                      const dispo = disponibiliteEquipes[equipe.id];
                      const total = dispo?.total || 0;
                      const disponibles = dispo?.disponibles || 0;

                      if (total === 0) {
                        return <span className="text-muted-foreground text-xs">—</span>;
                      }

                      // Tous disponibles
                      if (disponibles === total) {
                        return (
                          <span className="text-green-500 text-xs font-medium">
                            {total} sur {total} dispo
                          </span>
                        );
                      }

                      // Partiellement disponibles
                      return (
                        <span className={cn(
                          "text-xs",
                          disponibles > 0 ? "text-green-500 font-medium" : "text-zinc-400 font-medium"
                        )}>
                          {disponibles} sur {total} dispo
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="py-2">
                    <Badge variant="outline" className="font-semibold text-xs">
                      {equipe.membres_count || 0} {equipe.membres_count === 1 ? "membre" : "membres"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right py-2">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleEdit(equipe)}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleDelete(equipe.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
    </>
  );
};

export default TeamManager;
